import { startImpersonation as startImpersonationRequest } from "@/api/impersonationApi";
import { logOutUser } from "@/api/authApi";
import { MessageError } from "@/api/servises/ToastNotification";
import i18n from "@/plugins/i18n";

// localStorage key holding the admin's own identity while they impersonate another user,
// so it can be restored when impersonation stops.
const IMPERSONATOR_KEY = "hma_impersonator";

const setToken = ({ commit }, payload) => {
    localStorage.setItem("hma_extention_token", `Bearer ${ payload }`);
    commit("setToken", `Bearer ${ payload }`);
};

const removeToken = ( { commit } ) => {
    localStorage.removeItem("hma_extention_token");
    commit("removeToken");
};

const setUserRole = ( { commit }, payload ) => {
    localStorage.setItem("hma_user_role", payload );
    commit("setUserRole", payload)
};

const removeRole = ( { commit } ) => {
    localStorage.removeItem("hma_user_role");
    commit("removeRole");
};

const setUserInfo = ( { commit }, payload ) => {
    localStorage.setItem("hma_user_info", JSON.stringify(payload));
    commit("setUserInfo", payload);
};

const setUserName = ({ commit }, payload) => {
    let userInfo = JSON.parse(localStorage.getItem("hma_user_info"))
    const newUserInfo =  {...userInfo, name: payload}
    localStorage.setItem("hma_user_info", JSON.stringify(newUserInfo));
    commit("setUserInfo", newUserInfo);
}

const removeUserInfo = ( { commit } ) => {
    localStorage.removeItem("hma_user_info");
    commit("removeUserInfo");
};

// Start impersonating another user. The backend returns a token scoped to the target;
// we stash the admin's own identity first so it can be restored later, then hard-reload
// so the whole SPA rehydrates (token + role + userInfo) as the target user.
const startImpersonation = async ({ dispatch, getters }, userId) => {
    const impersonator = {
        token: getters.getToken.replace(/^Bearer\s+/i, ""),
        role: getters.getUserRole,
        userInfo: getters.getUserInfo,
    };

    let response;
    try {
        response = await startImpersonationRequest(userId);
    } catch (error) {
        return; // the global api error interceptor already surfaced the failure
    }

    const user = response.data.data;

    // mirror the login flow: refuse access-restricted accounts (e.g. waste_atlas/tsda)
    if (!user.access) {
        MessageError(i18n.t("messages.error.noAccess"));
        return;
    }

    localStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(impersonator));

    // swap to the target's identity (mirrors LoginButton.vue)
    dispatch("setUserInfo", {
        id: user.id,
        name: user.name,
        userable_id: user.userable_id,
        is_company: user.is_company,
        is_food_company: user.is_food_company,
        is_veas: user.is_veas,
        is_fodsa: user.is_fodsa,
    });
    dispatch("setToken", user.token);
    dispatch("setUserRole", (user.slug && user.slug[0]) || "");

    window.location.assign(window.location.origin);
};

// Stop impersonating: revoke the impersonation token, restore the admin's stashed identity,
// then hard-reload back into the admin session.
const stopImpersonation = async ({ dispatch }) => {
    const stored = localStorage.getItem(IMPERSONATOR_KEY);

    try {
        await logOutUser(); // revoke the current (impersonation) token on the backend
    } catch (error) {
        // even if revoking fails, still return to a clean state
    }

    localStorage.removeItem(IMPERSONATOR_KEY);

    if (stored) {
        // restore the admin's stashed identity
        const impersonator = JSON.parse(stored);
        dispatch("setToken", impersonator.token);
        dispatch("setUserRole", impersonator.role);
        dispatch("setUserInfo", impersonator.userInfo);
    } else {
        // stash lost — can't restore the admin; clear the session so the user is
        // never stranded (they land on the login page after the reload)
        dispatch("removeToken");
        dispatch("removeRole");
        dispatch("removeUserInfo");
    }

    // always hard-reload so the UI (and the stop button's loading state) resets
    window.location.assign(window.location.origin);
};

export default {
    setToken,
    setUserRole,
    removeToken,
    removeRole,
    setUserInfo,
    setUserName,
    removeUserInfo,
    startImpersonation,
    stopImpersonation
}